/**
 * en.ts — every string the console says, in English.
 *
 * **This file is the key definition.** `ar.ts` is typed against it, so a key
 * added here without an Arabic translation fails the build rather than falling
 * back silently at runtime. That is the whole reason this is a hand-written
 * catalogue rather than a JSON bundle loaded by an i18n library.
 *
 * ### Conventions
 *
 * - Keys are `area.thing`, lower-case, dot-separated. The area is the screen or
 *   the shared primitive, never the component file.
 * - `{name}` placeholders are interpolated by `translate()`. Names are words, not
 *   indices — `{count}`, `{module}` — because a translator moving them around a
 *   sentence must be able to tell which is which.
 * - **Counted strings end in a plural category**: `.one` and `.other` here, and
 *   Arabic adds `.zero`, `.two`, `.few` and `.many` in `ar.ts`. Call them through
 *   `useTn()`, never by concatenating a number onto a noun.
 * - Sections follow the order a reader meets them: shared vocabulary first, then
 *   the chrome, then screens.
 *
 * ### What does NOT belong here
 *
 * **Catalogue content.** A module's name, an item's prompt, a band label and a
 * persona's title are bilingual `{en, ar}` fields on the record, authored by the
 * people who own the content. They are resolved by `useContent()` against the
 * same locale, and putting one here would fork it from the data it came from.
 */

export const en = {
  /* ------------------------------------------------------------- vocabulary */
  'common.save': 'Save changes',
  'common.saving': 'Saving…',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.create': 'Create',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.retry': 'Try again',
  /**
   * Any screen whose list could not be fetched.
   *
   * It lived at `scoring.unreachable` and **Candidates was borrowing it** — a
   * string named for one screen, rendered on another. Invisible until Workstream
   * J made the scoring one specific to its registry and Candidates started
   * announcing the wrong thing. A generic message belongs in the generic
   * namespace.
   */
  'common.unreachable': 'Could not reach the API',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.confirm': 'Confirm',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'common.clear': 'Clear',
  'common.all': 'All',
  'common.none': 'None',
  'common.loading': 'Loading…',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.export': 'Export',
  'common.import': 'Import',
  'common.download': 'Download',
  'common.refresh': 'Refresh',
  'common.view': 'View',
  'common.open': 'Open',
  'common.done': 'Done',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.unknown': 'Unknown',
  'common.notAvailable': '—',
  'common.english': 'English',
  'common.arabic': 'Arabic',
  'common.minutes': '{minutes} min',
  'common.moreInfo': 'More information',

  /* ------------------------------------------------------------------ table */
  'table.errorTitle': 'Could not load this list',
  'table.errorDescription': 'The request failed.',
  'table.page': 'Page {page} of {pages} · {total} total',

  /* ------------------------------------------------------------------- nav */
  'nav.toggleSidebar': 'Toggle sidebar',
  'nav.group.overview': 'Overview',
  'nav.group.design': 'Assessment Design',
  'nav.group.delivery': 'Delivery',
  'nav.group.platform': 'Platform',
  'nav.dashboard': 'Dashboard',
  'nav.analytics': 'Analytics',
  'nav.modules': 'Modules',
  'nav.questions': 'Questions',
  'nav.questions.all': 'All Questions',
  'nav.questions.types': 'Question Types',
  'nav.scoring': 'Scoring Engine',
  // X7: the route renders the Matrix screen, titled 'Personas & Matrix'. The nav
  // label now says what the page it opens says.
  'nav.personas': 'Personas & Matrix',
  'nav.candidates': 'Candidates',
  'nav.qcReview': 'QC review',
  // V3: the sidebar badge's accessible sentence. The numeral beside the nav item
  // is `aria-hidden`; this is what a screen reader actually announces, and it is
  // part of the link's name rather than a separate utterance after it.
  'nav.qcReview.held.one': '{count} result held for review',
  'nav.qcReview.held.other': '{count} results held for review',
  // X9: was "Take an assessment", which in an operator's own sidebar reads as
  // *you* are about to be assessed. It is an internal QA instrument (PRD §2.2).
  'nav.take': 'Test run (QA)',
  'nav.notifications': 'Notifications',
  'nav.support': 'Support',
  'nav.settings': 'Settings',
  'nav.crumb.designQuestions': 'Assessment Design · Questions',

  /* ---------------------------------------------------------------- chrome */
  'chrome.brand': 'NAFIS Assessments',
  'chrome.brandSub': 'ETCC Admin',
  'chrome.searchPlaceholder': 'Search questions, candidates, modules…',
  'chrome.copyright': '© NAFIS · ETCC 2026',
  'chrome.openProfile': 'Open profile',
  'chrome.signOut': 'Sign out',
  'chrome.environmentTitle': 'This console is talking to the {environment} API',
  'chrome.themeToLight': 'Switch to light mode',
  'chrome.themeToDark': 'Switch to dark mode',
  'chrome.languageToArabic': 'Switch the console to Arabic',
  'chrome.languageToEnglish': 'Switch the console to English',
  'chrome.notifications': 'Notifications',
  'chrome.notificationsUnread': 'Notifications ({count} unread)',
  'chrome.notificationsEmpty':
    'Nothing to show. Notifications arrive once the platform can publish them — the read API over the change log is not built yet.',
  'chrome.viewAll': 'View all',
  'chrome.accept': 'Accept',
  'chrome.decline': 'Decline',
  'chrome.accepted.title': 'Access request accepted',
  'chrome.declined.title': 'Access request declined',

  /* ---------------------------------------------------- the topbar search --
   *
   * The placeholder above (`chrome.searchPlaceholder`) names three things, and
   * these strings must never describe a fourth. The panel searches question
   * prompts, module names and candidate references because those are the only
   * three the API can answer without a search endpoint; `search.scope` says so
   * in as many words, so an operator who finds nothing knows whether they asked
   * the wrong question or the wrong box.
   */
  'search.results': 'Search results',
  'search.searching': 'Searching…',
  'search.error': 'Could not run that search',
  'search.empty': 'Nothing matches “{query}”',
  'search.scope':
    'This box searches question prompts, module names and candidate references. Personas, results and settings are not searchable from here yet.',
  'search.group.questions': 'Questions',
  'search.group.modules': 'Modules',
  'search.group.candidates': 'Candidates',
  'search.seeAll.questions': 'See every question matching “{query}”',
  'search.seeAll.modules': 'See every module matching “{query}”',
  'search.seeAll.candidates': 'See every candidate matching “{query}”',

  /* ------------------------------------------------------------------ roles */
  'role.admin': 'Administrator',
  'role.analyst': 'Analyst',
  // Not a role the platform issues — it appears only in the RBAC policy table,
  // as the column that is not yet enforceable.
  'role.psychometrician': 'Psychometrician',

  /* ----------------------------------------------------------- error pages */
  'error.traceId': 'Trace ID:',
  'error.backToDashboard': 'Back to dashboard',
  'error.403.title': 'You do not have access',
  'error.403.description':
    'Your role does not permit this screen. Access is enforced server-side, so this is not something the interface can grant.',
  'error.404.title': 'Page not found',
  'error.404.description':
    'That route does not exist. It may have been renamed, or the screen may not have been built yet.',
  'error.500.title': 'Something went wrong',
  'error.500.description':
    'The server could not complete the request. If it keeps happening, quote the trace ID below to support.',

  /* ------------------------------------------------------------------ login */
  'login.brandSub': 'ETCC Administration',
  'login.headline': 'Psychometric assessment, composed not coded',
  'login.blurb':
    'Modules are data; psychometric models are plugins. Personas compose modules into blueprints, blueprints publish as plan versions, and every generated instance pins the versions it was built from.',
  'login.footer': 'UAE data residency · bilingual (EN / AR) · © NAFIS · ETCC 2026',
  'login.title': 'Welcome back',
  'login.subtitle': 'Sign in to the NAFIS administration console',
  'login.email': 'Email',
  'login.password': 'Password',
  // X6: was 'Forgot password?' rendered as a dead <span>. There is no reset in
  // the API and that is deliberate — users.ts refuses to let one operator set
  // another's password, because it would let an admin take an account silently.
  // So this says what actually recovers an account instead of implying a flow
  // that does not exist.
  'login.forgot': 'Locked out? An administrator issues a new account.',
  'login.signIn': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.unreachable': 'Could not reach the sign-in service.',
  'login.ssoNote': 'Which identity provider authenticates operators is set per deployment.',

  /* ---------------------------------------------------------------- profile */
  'profile.title': 'Profile',
  'profile.description': 'Your account and console preferences.',
  'profile.tab.account': 'Account',
  'profile.tab.appearance': 'Appearance',
  'profile.account.title': 'Account details',
  'profile.account.description': 'Your identity as the platform holds it.',
  'profile.name': 'Name',
  'profile.role': 'Role',
  'profile.notSignedIn': 'Not signed in',
  'profile.role.detail':
    'Assigned by an administrator, in Settings → Console users. Permissions are checked on the server, so what you can do never depends on what this screen shows you.',
  'profile.language': 'Console language',
  'profile.language.detail':
    'Changes this console only. A candidate is assessed in the language set when their assessment is generated — this cannot affect it.',
  'profile.language.saved': 'Console language updated',
  'profile.language.failed': 'Could not save your language',
  'profile.theme.title': 'Theme',
  'profile.theme.description':
    'Appearance is a device preference rather than an account setting, so it is configured once for the console in Settings.',
  'profile.theme.preset': 'Theme',
  'profile.theme.mode': 'Light or dark',
  'profile.theme.light': 'Light',
  'profile.theme.dark': 'Dark',
  'profile.theme.system': 'System',
  'profile.theme.changeInSettings': 'Change in Settings → Appearance',

  /* ------------------------------------------------------------ closed enums
   * The contract fixes these sets (`contract/registry.ts`). Rendered through
   * `useLabel()`, which falls back to the raw value for anything not here — and
   * `strings.test.ts` fails if a set is incomplete, so that fallback should
   * never fire.
   */
  'state.ready': 'Ready',
  'state.in_progress': 'In progress',
  'state.submitted': 'Submitted',
  'state.scoring': 'Scoring',
  'state.completed': 'Completed',
  'state.expired': 'Expired',
  'state.flagged': 'Flagged',
  'state.not_required': 'Not required',
  'state.pending': 'Pending',

  'status.draft': 'Draft',
  'status.review': 'In review',
  'status.certified': 'Certified',
  'status.retired': 'Retired',

  'moduleStatus.draft': 'Draft',
  'moduleStatus.active': 'Active',
  'moduleStatus.retired': 'Retired',

  'band.Low': 'Low',
  'band.Average': 'Average',
  'band.High': 'High',
  'skillBand.Emerging': 'Emerging',
  'skillBand.Proficient': 'Proficient',
  'skillBand.Advanced': 'Advanced',

  'qcFlag.straight_lining': 'Straight-lining',
  'qcFlag.speeding': 'Speeding',

  /* ------------------------------------------------------------- dimensions
   *
   * A dimension is NOT a closed enum — it is whatever a module's scoring config
   * declares — so this set is a best-effort overlay, not a contract, and
   * `useLabel`'s "render an unknown value as itself" fallback is doing real work
   * here rather than acting as a safety net.
   *
   * It exists because the WIRE keeps these in English by decision (2026-08-02):
   * `ScoringConfig.dimensionLabels` is one string per dimension, `/v1` publishes
   * it, and OpenInnovation designed against that. So the integration surface
   * stays English and the console translates for display — which is the cheap
   * direction, since it needs no contract change and no migration.
   *
   * The consequence to know: a module ETCC authors with new dimension keys will
   * show those keys until a row is added here. That is the same bargain
   * `useLabel` makes everywhere else, and it is visible rather than silent.
   */
  'dimension.R': 'Realistic',
  'dimension.I': 'Investigative',
  'dimension.A': 'Artistic',
  'dimension.S': 'Social',
  'dimension.E': 'Enterprising',
  'dimension.C': 'Conventional',
  'dimension.openness': 'Openness',
  'dimension.conscientiousness': 'Conscientiousness',
  'dimension.extraversion': 'Extraversion',
  'dimension.agreeableness': 'Agreeableness',
  'dimension.emotionalStability': 'Emotional Stability',
  'dimension.data_analysis': 'Data Analysis',
  'dimension.problem_solving': 'Problem Solving',
  'dimension.digital_literacy': 'Digital Literacy',

  /**
   * THE HEADLINE SENTENCE (N5) — the same bargain as `dimension.*` above, for
   * the most prominent string on a result.
   *
   * A plugin sends `display.headline.value` (an English sentence, which contract
   * v0 publishes and an integrator stores verbatim) plus a `key` naming one of
   * the templates below and `vars` carrying **keys, not prose**. The console
   * resolves the template and looks each var up — a dimension key through
   * `useDimensionLabel`, a band through the band catalogue — so the whole
   * sentence lands in the operator's language rather than being re-printed in
   * English with an Arabic frame around it.
   *
   * `features/result/headline.ts` falls back to `value` for a key that is not
   * here, so a plugin registered tomorrow reads as a report on day one and gets
   * its two lines here on day two. **Never a raw key on screen.**
   *
   * The placeholder names are the plugins' and are not ours to rename: they are
   * `vars` on the wire.
   */
  'headline.riasec.code_band': 'Holland code {hollandCode} — {band}',
  'headline.big_five.highest_lowest': 'Highest {highest}, lowest {lowest}',
  'headline.big_five.none': 'No traits scored',
  'headline.irt_bars.top_skill': 'Strongest skill {skill} — {band}',
  'headline.irt_bars.none': 'No skills scored',

  /* -------------------------------------------------------------- dashboard */
  'dashboard.description':
    'Programme-wide view across the three launch modules — every figure read from the results aggregate.',
  'dashboard.filterByModule': 'Filter by module',
  'dashboard.allModules': 'All modules',
  'dashboard.error': 'Could not load the dashboard',
  'dashboard.empty.title': 'No assessments yet',
  'dashboard.empty.description':
    'Tiles populate from real runs. Start one from Delivery → Take an assessment.',
  'dashboard.completions': 'Completions',
  'dashboard.scoredResults.one': '{count} scored result',
  'dashboard.scoredResults.other': '{count} scored results',
  'dashboard.inFlight.one': '{count} assessment in flight',
  'dashboard.inFlight.other': '{count} assessments in flight',
  'dashboard.completionRate': 'Completion rate',
  'dashboard.completionRate.hint': 'Completed ÷ every instance generated',
  'dashboard.qualityFlags': 'Quality flags',
  'dashboard.noFlags': 'No straight-lining or speeding detected',
  'dashboard.average': 'Average',
  'dashboard.overTime': 'Completions over time',
  'dashboard.overTime.hint': 'Scored results per week, most recent 26 weeks.',
  'dashboard.byDimension': 'Average score by dimension',
  'dashboard.byDimension.hint':
    '{module} — one instrument at a time, because dimension means from different models are different measures.',
  'dashboard.noModule': 'No module',
  'dashboard.noResultsForModule': 'No scored results for this module yet.',
  'dashboard.byModule': 'Completions by module',
  'dashboard.byModule.hint': 'Share of scored results across the three instruments.',
  'dashboard.viewMode': 'View mode',
  'dashboard.viewDonut': 'Donut',
  'dashboard.viewList': 'List',
  'dashboard.noResults': 'No scored results yet.',
  'dashboard.column.module': 'Module',
  'dashboard.column.state': 'State',
  'dashboard.column.count': 'Count',
  'dashboard.provenance':
    'Read from one $facet pipeline over results plus one $group over instances — no $lookup in either, which a test asserts against the pipelines as actually sent.',

  /* ---------------------------------------------------------------- modules */
  'modules.title': 'Assessment Modules',
  'modules.description': 'Each module binds a registered scoring plugin to a certified item bank.',
  'modules.count.one': '{count} module',
  'modules.count.other': '{count} modules',
  'modules.new': 'New module',
  'modules.loadFailed': 'Could not load modules',
  'modules.empty.title': 'No modules yet',
  'modules.empty.description':
    'A module declares its model type, which selects the scoring plugin, and embeds a versioned scoring config.',
  'modules.empty.action': 'Create the first module',
  'modules.searchPlaceholder': 'Search modules by name or id',
  'modules.noMatches': 'No modules match',
  'modules.noMatches.description': 'Nothing in the catalogue matches “{query}”.',
  'modules.configVersion': 'config v{version}',
  'modules.pluginVersion': 'plugin {version}',
  'modules.itemBank': 'Item bank',
  'modules.certifiedOf': '{certified} certified / {total}',
  'modules.duration': 'Duration',
  'modules.scoringConfig': 'Scoring configuration',
  'modules.lifecycleActions': 'Lifecycle actions for {module}',
  'modules.toggleActive': 'Offer {module} to candidates',
  'modules.currently': 'Currently {status}',
  'modules.statusHint.active': 'Offered — can enter a persona plan and be generated.',
  'modules.statusHint.draft': 'Authored, not offered. No new run can start on it.',
  'modules.statusHint.retired': 'Switched off permanently. Existing results stay readable forever.',
  'modules.action.publish': 'Publish — offer it to candidates',
  'modules.action.unpublish': 'Unpublish — stop new runs, reversibly',
  'modules.action.retire': 'Retire — permanent, never offered again',
  'modules.action.terminal': 'Retired is terminal — nothing brings it back',
  'modules.published': "'{module}' is active",
  'modules.published.detail': 'Every cell of this module can be filled.',
  'modules.published.amber.one': "'{module}' is active — but {count} cell is amber",
  'modules.published.amber.other': "'{module}' is active — but {count} cells are amber",
  'modules.published.amber.detail':
    'Those personas will generate a short instrument, or fail to generate. Certify more items or lower the cell counts.',
  'modules.publishFailed': 'Could not publish',
  'modules.unpublished': "'{module}' is back to draft",
  'modules.unpublished.breaking':
    'No new run can start on it. {plans} persona plan(s) still name it and will fail to generate: {names}. {open} run(s) already open are unaffected.',
  'modules.unpublished.clean':
    'No new run can start on it. {open} run(s) already open are unaffected — they froze their items and pinned their config.',
  'modules.unpublishFailed': 'Could not unpublish',
  'modules.retired': "'{module}' is retired",
  'modules.retired.detail':
    'It will never be presented again. Every result, instance and item stays exactly as it is.',
  'modules.retireFailed': 'Could not retire',

  /* ------------------------------------------------------ bulk (B5, B6) */
  'table.selectPage': 'Select every row on this page',
  'table.selectRow': 'Select this row',
  'bulk.selected.one': '{count} item selected',
  'bulk.selected.other': '{count} items selected',
  'bulk.clear': 'Clear selection',
  'bulk.actions': 'Bulk actions',
  'bulk.applying.one': 'Applying to {count} item…',
  'bulk.applying.other': 'Applying to {count} items…',
  'bulk.tags': 'Tags',
  'bulk.failed': 'The bulk operation failed',
  'bulk.applied.one': '{count} item {verb}',
  'bulk.applied.other': '{count} items {verb}',
  'bulk.applied.detail': 'Every item in the selection changed.',
  'bulk.partial': '{applied} of {requested} {verb}',
  'bulk.newlyAmber.one': '{count} cell is now amber',
  'bulk.newlyAmber.other': '{count} cells are now amber',
  'bulk.verb.certify': 'certified',
  'bulk.verb.uncertify': 'returned to draft',
  'bulk.verb.retire': 'retired',
  'bulk.verb.tagged': 'updated',
  'bulk.tags.title': 'Tag the selection',
  'bulk.tags.description.one': 'Applies to {count} selected item.',
  'bulk.tags.description.other': 'Applies to {count} selected items.',
  'bulk.addTags': 'Add tags (comma separated)',
  'bulk.removeTags': 'Remove tags (comma separated)',
  'bulk.audienceTags': 'Audience tags — replaces',
  'bulk.contextTags': 'Context tags — replaces',
  'bulk.replaceWarning':
    'Audience and context tags REPLACE what each item already has, because they decide which matrix cells an item can be drawn into. Ordinary tags are added and removed.',
  'bulk.applyTags': 'Apply to selection',

  /* ---------------------------------------------------------------- results */
  'nav.results': 'Results',
  'results.description':
    'Every scored result, newest first. A re-score adds a row rather than replacing one.',
  'results.column.flags': 'Quality flags',
  'results.firstScoring': 'first scoring',
  'results.searchPlaceholder': 'Search by candidate reference…',
  'results.empty.title': 'No results yet',
  'results.empty.description':
    'A result appears here when an assessment is submitted and scored. Start one from Delivery → Take an assessment.',
  'results.noMatches': 'No results match',
  'results.noMatches.description': 'Nothing matches these filters. Clear them to see every result.',

  /* ------------------------------------------------------------ list state */
  'filters.saved.label': 'Views',
  'filters.thisScreen': 'Saved for this screen',
  'filters.none': 'Nothing saved yet. Filter the list, then save the view.',
  'filters.saveCurrent': 'Save this view',
  'filters.name': 'View name',
  'filters.namePlaceholder': 'Certified behavioural',
  'filters.save': 'Save',
  'filters.saved': 'Saved “{name}”',
  'filters.remove': 'Remove {name}',
  'questions.empty.clearFilters': 'Clear filters',
  'chrome.breadcrumb': 'Breadcrumb',

  /* -------------------------------------------------------------- questions */
  'questions.description':
    'The bilingual item bank. Only certified items are sampled into an assessment.',
  'questions.column.prompt': 'Prompt',
  'questions.column.module': 'Module',
  'questions.column.type': 'Type',
  'questions.column.status': 'Status',
  'questions.filterByType': 'Filter by type',
  'questions.filterByStatus': 'Filter by status',
  'questions.allTypes': 'All types',
  'questions.allStatuses': 'All statuses',
  'questions.searchPlaceholder': 'Search either language…',
  'questions.searchLabel': 'Search prompts',
  'questions.empty.title': 'No items match',
  'questions.empty.description':
    'Nothing in the bank matches these filters. Clear them, or author a new item.',
  'questions.newItem': 'New item',
  'questions.exportCsv': 'Export CSV',
  'questions.exporting': 'Exporting…',
  'questions.importCsv': 'Import CSV',
  'questions.export.ready': 'Export ready',
  'questions.export.hint': 'Edit it in Excel and import it back — rows keep their itemId.',
  'questions.export.failed': 'Could not export',

  'questions.action.review': 'Review',
  'questions.action.certify': 'Certify',
  'questions.action.uncertify': 'Un-certify',
  'questions.action.retire': 'Retire',
  'questions.action.unretire': 'Un-retire',
  'questions.failed.review': 'Could not send for review',
  'questions.failed.certify': 'Could not certify',
  'questions.failed.uncertify': 'Could not un-certify',
  'questions.failed.retire': 'Could not retire',
  'questions.failed.unretire': 'Could not un-retire',
  'questions.sentForReview': 'Sent for review',
  'questions.sentForReview.detail': '{item} is awaiting a reviewer.',
  'questions.certified': 'Item certified',
  'questions.certified.detail': '{item} is now sampleable into new assessments.',
  'questions.unretired': 'Item un-retired',
  'questions.unretired.detail': '{item} is a draft again — certify it to make it sampleable.',
  'questions.impact.uncertified': '{item} returned to draft',
  'questions.impact.retired': '{item} retired',
  // `.amber` and not `.one`/`.other` directly on the verb: the plural base is
  // everything before the category, and `questions.impact.retired` is already a
  // key in its own right.
  'questions.impact.uncertified.amber.one': '{item} returned to draft — {count} cell is now amber',
  'questions.impact.uncertified.amber.other': '{item} returned to draft — {count} cells are now amber',
  'questions.impact.retired.amber.one': '{item} retired — {count} cell is now amber',
  'questions.impact.retired.amber.other': '{item} retired — {count} cells are now amber',
  'questions.impact.amber.detail':
    '{cells} can no longer fill from the certified bank. Generation will refuse those personas until the pool recovers.',
  'questions.impact.pinned.one':
    'Still green, but {count} cell pinned this item explicitly: {cells}.',
  'questions.impact.pinned.other':
    'Still green, but {count} cells pinned this item explicitly: {cells}.',
  'questions.impact.none': 'No matrix cell changed colour.',

  'questions.editTitle': 'Edit {item}',
  'questions.editHint':
    'Editing cannot reach an assessment that already froze this item. Its lifecycle state is unchanged by saving.',
  'questions.newHint': 'Authored as a draft. Only certified items are sampled into an assessment.',
  'questions.prompt.en': 'Prompt (English)',
  'questions.prompt.ar': 'Prompt (Arabic)',
  'questions.label.en': 'Label (English)',
  'questions.label.ar': 'التسمية (بالعربية)',
  'questions.dimension': 'Dimension',
  'questions.scaleMin': 'Scale min',
  'questions.scaleMax': 'Scale max',
  'questions.anchors.en': 'Scale anchors (English, | separated)',
  'questions.anchors.ar': 'Scale anchors (Arabic, | separated)',
  'questions.anchors.placeholder': 'Strongly disagree | … | Strongly agree',
  'questions.reverseKeyed': 'Reverse-keyed — the scale is inverted before scoring',
  'questions.tags': 'Tags (comma separated)',
  'questions.skillId': 'Skill id (optional)',
  'questions.createDraft': 'Create draft item',
  'questions.saved': 'Item saved',
  'questions.saved.detail': '{item} updated. Instances that already froze it are unaffected.',
  'questions.created': 'Item created',
  'questions.created.detail': '{item} — draft, not yet sampleable. Certify it to release it.',
  'questions.saveFailed': 'Could not save the item',

  'questions.options': 'Options',
  'questions.optionsOf': '{count} of {max} (G1)',
  'questions.option.add': 'Add option',
  'questions.option.moveUp': 'Move option {n} up',
  'questions.option.moveDown': 'Move option {n} down',
  'questions.option.remove': 'Remove option {n}',
  'questions.option.labelEn': 'Option {n} label (English)',
  'questions.option.labelAr': 'Option {n} label (Arabic)',
  'questions.option.isCorrect': 'Option {n} is correct',
  'questions.option.correctAnswer': 'Correct answer',
  'questions.option.value': 'Effectiveness value',
  'questions.option.valueLabel': 'Option {n} effectiveness value',
  'questions.option.anchor': 'Anchor level (1 = lowest)',
  'questions.option.anchorLabel': 'Option {n} anchor level',
  'questions.option.dimension': 'Dimension this option scores',
  'questions.option.dimensionLabel': 'Option {n} dimension',

  'questions.problem.promptEn': 'The English prompt is required.',
  'questions.problem.noFieldSet': "No field set for item type '{type}'.",
  'questions.problem.dimension': 'A scale item must name the dimension it scores.',
  'questions.problem.scaleRange': 'Scale max must be greater than scale min.',
  'questions.problem.anchorsBoth':
    'Anchors need at least two labels in BOTH languages, or none at all.',
  'questions.problem.minOptions': '{type} needs at least {min} options.',
  'questions.problem.maxOptions': '{type} allows at most {max} options.',
  'questions.problem.optionLabel': 'Option {n} has no English label.',
  'questions.problem.optionValue': 'Option {n} needs an effectiveness value.',
  'questions.problem.optionAnchor': 'Option {n} needs an anchor level.',
  'questions.problem.optionDimension': 'Option {n} needs a dimension.',
  'questions.problem.oneCorrect': 'Exactly one option must be marked correct.',

  /* ------------------------------------------------- where an item fits (B6) */
  'questions.cells.title': 'Draws into',
  'questions.cells.detail':
    'The matrix cells whose pool query this item matches, from its module, tags, audience and context. Change a tag above and this changes with it.',
  'questions.cells.none':
    'No cell draws this item. Its module, tags or audience do not match any pool query.',
  'questions.cells.pinned': 'pinned',
  'questions.cells.notCertified':
    'It will only be drawn once it is certified — these are the cells it would enter.',

  /* ------------------------------------------------------------- item types */
  'itemType.likert': 'Likert',
  'itemType.likert.hint':
    'An agreement scale on one dimension. Anchors label what each point means.',
  'itemType.slider': 'Slider',
  'itemType.slider.hint':
    'A continuous scale on one dimension, normalised against the module’s other scales.',
  'itemType.forced_choice': 'Forced choice',
  'itemType.forced_choice.hint':
    'Ipsative: each option scores a different dimension, so choosing one declines the others.',
  'itemType.ranking': 'Ranking',
  'itemType.ranking.hint':
    'The answer is an order, not a choice. Every option scores a dimension by its position.',
  'itemType.mcq': 'Multiple choice',
  'itemType.mcq.hint': 'Exactly one option is correct.',
  'itemType.sjt': 'Situational judgement',
  'itemType.sjt.hint':
    'Every option carries an effectiveness value — there is no single right answer.',
  'itemType.bars': 'BARS',
  'itemType.bars.hint': 'Behaviourally anchored: each option describes a level of performance.',

  /* ---------------------------------------------------------------- scoring */
  'scoring.description':
    'How each module turns answers into scores — dimensions, interpretation bands, reference groups and quality rules. Data, not code.',
  'scoring.configure': 'Configure',
  'scoring.normGroups.one': '{count} reference group',
  'scoring.normGroups.other': '{count} reference groups',
  /* Not an error and not styled as one: no reference group means every result
     reports a band and no percentile, which is accurate. */
  'scoring.noNormGroups': 'no reference groups',
  'scoring.filtered': 'Showing {module} only',
  'scoring.showAll': 'Show all modules',
  'scoring.noModules': 'No modules to configure',
  'scoring.noModules.description':
    'A scoring config belongs to a module, so this screen lists one row per module. Create a module and its config comes with it.',

  /* The engine registry, demoted to reference material. A plugin is code
     registered at boot; it has no settings, because its settings are the config
     on each module above. */
  'scoring.engines': 'Installed scoring models',
  'scoring.engines.hint':
    'Read live from GET /api/health. A model is code registered at boot and has no settings of its own — what is configurable is each module’s config above. Pick one when you create a module.',
  'scoring.engine.unused': 'No modules use this model.',
  'scoring.engine.modules.one': '{count} module',
  'scoring.engine.modules.other': '{count} modules',
  'scoring.engine.active': '{count} active',
  'scoring.unreachable': 'Could not reach the scoring registry',
  'scoring.empty.description':
    'The API is reachable but its registry is empty, so this instance cannot score a submitted assessment. Plugins register at boot.',
  'scoring.model.unknown': 'Registered scoring model.',
  'scoring.model.RIASEC':
    'Holland vocational interests — six themes reported as a Holland code, drawn on the hexagon where neighbouring themes are the most alike.',
  'scoring.model.BIG_FIVE':
    'Big Five / OCEAN traits with interpretation bands and quality flags. Reports a percentile against the reference group where one is configured.',
  'scoring.model.IRT_BARS':
    'IRT-informed knowledge items plus BARS self-anchors, scored per skill.',

  /* ------------------------------------------------------- batch re-score */
  'rescore.open': 'Re-score a cohort',
  'rescore.title': 'Re-score a cohort',
  'rescore.description':
    'Re-score finished runs under the current config and plugin build. Preview first — nothing is written until you apply it.',
  'rescore.module': 'Module',
  'rescore.persona': 'Persona',
  'rescore.allModules': 'All modules',
  'rescore.allPersonas': 'All personas',
  'rescore.appendOnly':
    'Nothing is overwritten. Each re-scored run gets a new result that names the one it supersedes, and the old result stays readable.',
  'rescore.dryRun': 'Preview',
  'rescore.dryRunFirst': 'Preview first — it reports what would change before anything is written',
  'rescore.checking': 'Checking…',
  'rescore.apply': 'Re-score {count}',
  'rescore.applying': 'Re-scoring…',
  'rescore.done': 'Cohort re-scored',
  'rescore.done.detail': '{changed} of {examined} runs produced a different score.',
  'rescore.failed': 'Re-score failed',
  'rescore.noMatches': 'No finished runs match this filter, so there is nothing to re-score.',
  'rescore.rowsHint': 'Runs whose score would move, or that could not be scored:',
  'rescore.truncated':
    '{matched} runs match and only {examined} were examined — this call is capped. Filter more narrowly to cover the rest.',
  'rescore.stat.examined': 'examined',
  'rescore.stat.changed': 'would change',
  'rescore.stat.unchanged': 'unchanged',
  'rescore.stat.failed': 'failed',
  'rescore.outcome.changed': 'changed',
  'rescore.outcome.unchanged': 'unchanged',
  'rescore.outcome.failed': 'failed',
  'rescore.willHold': 'would go on QC hold',
  'rescore.willRelease': 'would come off QC hold',
  /* Its own alert rather than a fifth statistic: a hold is a different kind of
     fact from a count, and burying it in a row of numbers is how forty runs
     enter the QC queue without the person who sent them there noticing. */
  'rescore.held.one': '{count} run would be put on QC hold and its score withheld pending review.',
  'rescore.held.other': '{count} runs would be put on QC hold and their scores withheld pending review.',
  'rescore.unheld.one': '{count} run would come off QC hold.',
  'rescore.unheld.other': '{count} runs would come off QC hold.',

  /* --------------------------------------------------------- question types */
  'questionTypes.description':
    'The item-type registry — which modules use each type, and how it scores.',
  'questionTypes.count.one': '{count} type',
  'questionTypes.count.other': '{count} types',
  'questionTypes.loadFailed': 'Could not load the registry',
  'questionTypes.certifiedCount': '{count} certified',
  'questionTypes.usedBy': 'Used by',
  'questionTypes.unused': 'No module declares this type.',
  'questionTypes.breakdown': '{total} in the bank · {draft} draft · {retired} retired',
  'questionTypes.undescribed': 'A type present in the bank but not described here.',
  'questionTypes.scoredByPlugin': 'Scored by whichever plugin its module binds.',
  'questionTypes.openRegistry':
    'Types are a runtime registry, not a code enum — the contract validates itemType as a format-checked string against the live set, which is what lets a new type appear without a deployment. A type found in the bank but not described above still shows here, because that is exactly the case an open registry is for.',
  'questionTypes.noToggle':
    'There is no enable/disable toggle: gating which types may be authored needs a stored set of enabled types, and no such collection exists yet. A switch that looked authoritative and changed nothing would be worse than its absence.',
  'questionTypes.answer.likert': 'One point on an agreement scale.',
  'questionTypes.scoring.likert':
    'Adds to its dimension, zero-based, inverted first when reverse-keyed.',
  'questionTypes.answer.slider': 'A continuous position on a scale.',
  'questionTypes.scoring.slider':
    'Normalised against its own range, so a 0–100 slider and a 1–5 likert weigh the same.',
  'questionTypes.answer.forced_choice': 'Pick one of two or three.',
  'questionTypes.scoring.forced_choice':
    'Ipsative — the chosen option’s dimension gains; the others were available and did not.',
  'questionTypes.answer.ranking': 'Place every option in order.',
  'questionTypes.scoring.ranking': 'Position-weighted: first place is worth N, last is worth 1.',
  'questionTypes.answer.mcq': 'Pick one option; exactly one is correct.',
  'questionTypes.scoring.mcq': 'Correct or not, weighted by the item’s discrimination.',
  'questionTypes.answer.sjt': 'Pick the best response to a situation.',
  'questionTypes.scoring.sjt':
    'The chosen option’s effectiveness value, over the best available on that item.',
  'questionTypes.answer.bars': 'Pick the behavioural anchor that fits.',
  'questionTypes.scoring.bars': 'The anchor’s level, placed across the scale as (level−1)/(k−1).',

  /* ------------------------------------------------------------- candidates */
  'candidates.description':
    'Assessment instances by candidate reference — opaque identifiers only, no PII.',
  'candidates.count.one': '{count} candidate',
  'candidates.count.other': '{count} candidates',
  'candidates.inFlight': '{count} in flight',
  'candidates.feedSilent': 'The instance feed did not answer.',
  'candidates.empty.title': 'No candidates yet',
  'candidates.empty.description':
    'Candidates are tracked by an opaque candidateRef so the platform holds no personally identifying data. Instances appear here once the pipeline runs.',
  'candidates.searchPlaceholder': 'Search by candidate reference, module or persona…',
  'candidates.noMatches': 'No matches',
  'candidates.noMatches.description': 'Nothing matches “{query}”.',
  'candidates.instances.one': '{count} instance',
  'candidates.instances.other': '{count} instances',
  'candidates.results.one': '{count} result',
  'candidates.results.other': '{count} results',
  'candidates.answered': '{answered}/{total} answered ({progress}%)',
  // X1: the row's only link out used to be the raw result id pointing at
  // /api/results/… — which answered 401 as JSON. A destination, not an id.
  'candidates.viewResult': 'View result',
  'candidate.title': 'Candidate',
  'candidate.journey': 'Assessment plan',
  'candidate.journey.hint':
    'Every module this candidate was pinned to sit, in plan order — including the ones they have not started.',
  'candidate.completeOf': '{completed} of {total} complete',
  'candidate.notStarted': 'Not started',
  'candidate.resume': 'Resume run',
  'candidate.notFound': 'No such candidate',
  'candidate.backToList': 'Back to candidates',
  'candidate.noPlan': 'No plan to show',
  'candidate.noPlan.hint':
    'This candidate has runs but their persona names no modules. Publish a plan for the persona to see a journey here.',
  'candidate.unregistered':
    'Resolved from this candidate’s runs — they were never registered through the integration API, which is normal for a test run started from this console.',
  'candidate.planUnresolvable':
    'Plan version {version} could not be found in the persona’s retained history, so the plan below is today’s, not the one this candidate was pinned to.',
  'candidate.lineage': 'Result history',
  'candidate.lineage.hint':
    'Results are append-only — a re-score writes a new one and keeps the old. This is every result for this candidate, newest first.',
  'candidate.registeredAt': 'Registered {when}',
  'candidates.provenance':
    'Read live from GET /api/assessments and GET /api/results. A run appears the moment it is generated — the results feed alone could not show one in progress.',

  /* ----------------------------------------------------------- persona editor */
  'personaEditor.loading': 'Loading persona…',
  'personaEditor.description':
    'A plan edit is a draft until you publish it. Publishing mints a new version and keeps the old one resolvable, so a run already generated still knows what it was administered under.',
  'personaEditor.unpublishedDraft': 'unpublished draft',
  'personaEditor.segment': 'Segment',
  'personaEditor.defaultLocale': 'Default administration language',
  'personaEditor.saveDetails': 'Save details',
  'personaEditor.detailsSaved': 'Details saved',
  'personaEditor.detailsSaved.detail': 'Names and segment are labels — nothing is pinned to them.',
  'personaEditor.saveFailed': 'Could not save',
  'personaEditor.planTemplate': 'Plan template',
  'personaEditor.editingDraft': 'editing the unpublished draft',
  'personaEditor.editingCopy': 'editing a copy of {version}',
  'personaEditor.addModule': 'Add module',
  'personaEditor.emptyPlan':
    'This plan names no modules. Published as it stands, every candidate of this persona would be refused — retire the persona instead if that is what you mean.',
  'personaEditor.moduleAt': 'Module at position {n}',
  'personaEditor.itemCountFor': 'Item count for {module}',
  'personaEditor.required': 'Required',
  'personaEditor.requiredFor': '{module} required',
  'personaEditor.moveUp': 'Move {module} up',
  'personaEditor.moveDown': 'Move {module} down',
  'personaEditor.remove': 'Remove {module}',
  'personaEditor.itemCountNote':
    'The counts here are this plan’s defaults. Where a matrix cell exists it decides what is actually asked, and the preview says so when the two differ.',
  'personaEditor.saveDraft': 'Save draft',
  'personaEditor.draftSaved': 'Draft saved',
  'personaEditor.draftSaved.detail':
    'Nothing resolves against a draft. Candidates keep sitting the published plan until you publish this one.',
  'personaEditor.draftSaveFailed': 'Could not save the draft',
  'personaEditor.discardDraft': 'Discard draft',
  'personaEditor.discarded': 'Draft discarded',
  'personaEditor.discarded.detail': 'Back to {version}, which never stopped being in force.',
  'personaEditor.discardFailed': 'Could not discard',
  'personaEditor.publish': 'Publish',
  'personaEditor.unsaved':
    'There are unsaved changes above. Save the draft before previewing, or the preview will describe the plan you started from.',
  'personaEditor.previewAction': 'Preview changes and coverage',
  'personaEditor.previewFailed': 'Could not preview',
  'personaEditor.willMint':
    'Publishing mints {next} and supersedes {current}, which stays resolvable.',
  'personaEditor.nothingToPublish':
    'Nothing to publish — this is the coverage of {current} as it stands.',
  'personaEditor.pinnedRuns':
    '{open} open and {completed} completed runs are pinned to {version}. They are unaffected — the version they pinned stays resolvable.',
  'personaEditor.whatChanges': 'What changes',
  'personaEditor.change.added': 'added',
  'personaEditor.change.changed': 'changed',
  'personaEditor.change.removed': 'removed',
  'personaEditor.change.unchanged': 'unchanged',
  'personaEditor.coverage': 'Coverage, against this plan',
  'personaEditor.asksFor': 'asks for {count}',
  'personaEditor.superseded': 'Superseded versions',
  'personaEditor.superseded.hint':
    'Kept whole, not merely noted. A run pinned to one of these can still be told exactly what it was administered under.',
  'personaEditor.noModules': 'no modules',
  'personaEditor.publishPlan': 'Publish plan',
  'personaEditor.publishing': 'Publishing…',
  'personaEditor.published': 'Plan {version} is in force',
  'personaEditor.published.detail':
    'The superseded version stays resolvable for every run pinned to it.',
  'personaEditor.publishFailed': 'Could not publish',
  'personaEditor.create.description':
    'An audience, and the plan of modules it sits. The plan comes next — a persona is created with none, because a plan is only meaningful against the matrix cells and certified bank behind it.',
  'personaEditor.personaId': 'Persona id',
  'personaEditor.personaId.invalid':
    'Dotted lower_snake, 4–64 characters — e.g. persona.high_schooler.',
  'personaEditor.createPersona': 'Create persona',
  'personaEditor.created': "'{persona}' created",
  'personaEditor.created.detail':
    'It administers nothing yet. Add modules to its plan, check the coverage, then publish.',
  'personaEditor.createFailed': 'Could not create',

  /* ------------------------------------------------- module create wizard */
  'createModule.title': 'New assessment module',
  'createModule.description':
    'A module binds a registered scoring plugin to an item bank. Adding a new psychometric model is a code deployment, not a form — only models already deployed appear below.',
  'createModule.step1': 'Identity',
  'createModule.step1.hint': 'Bilingual, like every content field on this platform.',
  'createModule.moduleId': 'Module id',
  'createModule.moduleId.detail':
    'Lower-case letters and underscores, 3–64 characters. Permanent once created, because every result refers back to it.',
  'createModule.moduleId.invalid': 'Must match lower_snake_case, 3–64 characters.',
  'createModule.minutes': 'Estimated minutes',
  'createModule.name.en': 'Name (English)',
  'createModule.name.ar': 'Name (Arabic)',
  'createModule.description.en': 'Description (English)',
  'createModule.description.ar': 'Description (Arabic)',
  'createModule.step2': 'Scoring model',
  'createModule.step2.hint':
    'Read from the live plugin registry. This choice decides which code scores every result.',
  'createModule.scores': 'Scores {types}',
  'createModule.buildsRetained': '{count} builds retained',
  'createModule.noPlugins':
    'No scoring plugin is registered, so no module can be created. Plugins register at boot — this instance cannot score anything.',
  'createModule.step3': 'Supported item types',
  'createModule.step3.hint':
    'Constrained by the chosen model — an item its plugin cannot score is refused at authoring time.',
  'createModule.needAType':
    'At least one type — a module that supports none can never hold an item.',
  'createModule.step4': 'Scoring configuration',
  'createModule.step4.hint':
    'Seeded from the model’s own shape, and validated by its plugin on save.',
  'createModule.noTemplate':
    '{model} publishes no starting config, so this begins empty. Its plugin will still validate whatever you enter.',
  'createModule.version1': 'version 1',
  'createModule.versionShort': 'Set automatically',
  'createModule.versionNote':
    'Set server-side and never editable here — the version is what an instance pins.',
  'createModule.nothingCreated': 'Nothing was created:',
  'createModule.createDraft': 'Create as draft',
  'createModule.created': "Module '{module}' created as a draft",
  'createModule.created.detail':
    'Author and certify its bank, then publish it — publishing reports the coverage it lands in.',
  'createModule.badJson': 'Scoring config is not valid JSON: {error}',
  'createModule.failed': 'Create failed',

  /* ------------------------------------------------------ scoring config editor */
  'config.title': 'Scoring configuration — {module}',
  'config.description':
    'Data, not code. Validated by this module’s own scoring plugin, and the version bumps only when a change can alter a score.',
  'config.versionHistory': 'Version history',
  'config.jsonLabel': 'Scoring configuration JSON',
  'config.absent': '(absent)',
  'config.changesScores': 'changes scores',
  'config.displayOnly': 'display only',
  'config.noHistory':
    'No superseded versions. This module has only ever scored under v{version}.',
  'config.history.hint':
    'Read-only, and deliberately so. A superseded config is what an in-flight run resolves when it submits — editing one would rewrite what a run already under way is being administered under. To change how scoring works, edit the current version; to apply it to a run that has already finished, re-score.',
  'config.dimensionsBands': '{dimensions} dimensions · {bands} bands',
  'config.notJson': 'Not valid JSON',
  'config.notJsonDetail': 'Not valid JSON: {error}',
  'config.requestFailed': 'Request failed',
  'config.rejected': 'This configuration was rejected and nothing was saved:',
  'config.identical': 'This is identical to the saved configuration — nothing would change.',
  'config.versions': 'This versions the config: v{from} → v{to}.',
  'config.pinnedOpen.one':
    '{count} run in flight is pinned to v{version} and will still be scored under it — v{version} is retained in the version history, not replaced.',
  'config.pinnedOpen.other':
    '{count} runs in flight are pinned to v{version} and will still be scored under it — v{version} is retained in the version history, not replaced.',
  'config.pinnedCompleted.one':
    '{count} finished result recorded it and is never recomputed; re-score is the only way a new version reaches it.',
  'config.pinnedCompleted.other':
    '{count} finished results recorded it and are never recomputed; re-score is the only way a new version reaches them.',
  'config.displayOnlyChange':
    'Display-only change — the version stays at v{version}, so no in-flight run is affected.',
  'config.checking': 'Checking…',
  'config.preview': 'Preview changes',
  'config.previewFirst': 'Preview the change first — it reports what the save would version',
  'config.save': 'Save',
  'config.saved': 'Scoring config saved',
  'config.saved.detail':
    'Version {version} — instances already generated keep the version they pinned.',

  /* -------------------------------------------- structured scoring-config form */
  'configForm.tab': 'Settings',
  'configForm.json': 'JSON',
  'configForm.unparseable':
    'The JSON tab holds something that is not valid JSON, so this form cannot read it. Fix it there, or discard the change by closing this dialog.',

  'configForm.dimensions': 'Dimensions',
  'configForm.dimensions.hint':
    'What this module scores. Adding or removing one changes scores, so it versions the config.',
  'configForm.dimensions.fixed':
    'This model defines its own dimensions and will reject any other set, so they cannot be added, removed or reordered here. Their labels are yours.',
  'configForm.dimensions.add': 'Add dimension',
  'configForm.dimensions.key': 'Dimension key',
  'configForm.dimensions.label': 'Label for {key}',
  'configForm.dimensions.remove': 'Remove {key}',
  'configForm.dimensions.english':
    'Labels stay in English: they are published on the integration API, where an integrator stores them verbatim. The console shows Arabic from its own catalogue.',

  'configForm.bands': 'Interpretation bands',
  'configForm.bands.hint':
    'How a 0–100 score becomes a word. Every score must fall in exactly one band.',
  'configForm.bands.add': 'Add band',
  'configForm.bands.strip': 'Band coverage across the 0–100 range',
  'configForm.bands.uncovered': 'Nothing covers {from}–{to}',
  'configForm.bands.label': 'Label for band {index}',
  'configForm.bands.pickLabel': 'Pick a label',
  'configForm.bands.min': 'Lowest score in band {index}',
  'configForm.bands.max': 'Highest score in band {index}',
  'configForm.bands.remove': 'Remove band {index}',
  'configForm.bands.issues': 'These bands do not cover 0–100 cleanly:',
  'configForm.bands.inclusive':
    'Both ends are included, so 0–39 and 40–69 sit side by side with nothing between them — and 0–39 with 39–69 would claim 39 twice.',

  'configForm.norms': 'Reference groups',
  'configForm.norms.hint':
    'What a score is compared against. With a table here, every result reports a percentile and names the group it was measured against; without one it reports the band alone.',
  'configForm.norms.persona': 'Persona',
  'configForm.norms.pickPersona': 'Pick a persona',
  'configForm.norms.add': 'Add table',
  'configForm.norms.none':
    'No reference groups. Results from this module report a band and no percentile, which is accurate — nothing has been calibrated yet.',
  'configForm.norms.removeTable': 'Remove',
  'configForm.norms.dimension': 'Dimension',
  'configForm.norms.mean': 'Mean',
  'configForm.norms.sd': 'SD',
  'configForm.norms.scale':
    'Mean and standard deviation on the same 0–100 scale as the score. Clear a row to leave that dimension uncalibrated — it will report its band and no percentile.',
  'configForm.norms.sdPositive': 'SD must be above zero',
  'configForm.norms.meanFor': 'Mean for {dimension}',
  'configForm.norms.sdFor': 'Standard deviation for {dimension}',
  'configForm.norms.clear': 'Clear the norm for {dimension}',

  'configForm.gates': 'Completion and quality gates',
  'configForm.gates.hint':
    'When a run may be submitted, and what makes its answers suspect. All three change scores.',
  'configForm.minCompletion': 'Minimum completion',
  'configForm.minCompletion.hint': 'Below this, submitting is refused. 100 means every item.',
  'configForm.straightLine': 'Straight-lining threshold',
  'configForm.straightLine.hint':
    'A proportion, not a percentage: 0.8 flags a run where 80% of the scale answers are identical.',
  'configForm.fastMs': 'Fast-response threshold',
  'configForm.fastMs.hint':
    'Milliseconds. A run whose median answer is faster than this is flagged for review.',

  'configForm.options': 'Model settings',
  'configForm.options.hint': 'Knobs this scoring model reads, as it declares them.',
  'configForm.options.pick': 'Pick a value',

  /* ------------------------------------------------------------- CSV import */
  'import.title': 'Import items from CSV',
  'import.description':
    'Nothing is written until you commit. Rows with an itemId update that item; rows without one are inserted.',
  'import.csvFile': 'CSV file',
  'import.checking': 'Checking the file…',
  'import.unrecognised': '{count} columns are not recognised',
  'import.unrecognised.hint':
    'An unmapped column is ignored. If one of these is a prompt or a scoring key, mapping it is the difference between an import and an import that blanks a field.',
  'import.mapColumn': 'Map column {header}',
  'import.ignoreColumn': 'Ignore this column',
  'import.problems': '{count} problems',
  'import.allOrNothing':
    'Nothing will be written until every row is good — a half-applied import cannot be re-run without duplicating.',
  'import.rowError': 'Row {row}{path} — {message}',
  'import.andMore': '…and {more} more.',
  'import.column.row': 'Row',
  'import.column.item': 'Item',
  'import.newRow': '— new —',
  'import.truncated': 'Showing the first 100 of {total} rows. All of them are checked.',
  'import.willIgnore': '{count} columns will be ignored.',
  'import.applied': 'Import applied',
  'import.applied.detail':
    '{created} created, {updated} updated. Nothing else in the bank was touched.',
  'import.action.create': 'create',
  'import.action.update': 'update',
  'import.action.skip': 'skip',
  'import.action.error': 'error',

  /* ----------------------------------------------------------- console users */
  'users.column.user': 'User',
  'users.column.lastSignIn': 'Last sign-in',
  'users.never': 'never',
  'users.status.active': 'active',
  'users.status.deactivated': 'deactivated',
  // Replaced the Make-admin / Make-analyst pair: a two-state toggle could not
  // express a three-value enum, so the row now carries a role select.
  'users.changeRole': 'Change role',
  'users.deactivate': 'Deactivate',
  'users.reactivate': 'Reactivate',
  'users.newUser': 'New user',
  'users.enforcementNote':
    'Roles are enforced server-side on every request — hiding a control in the UI is not a security boundary. The platform refuses to deactivate or demote its last administrator.',
  'users.empty.title': 'No console users',
  'users.empty.description':
    'The first administrator is created by the create-admin CLI command.',
  'users.updated': 'User updated',
  'users.updated.detail': '{email} — {role}, {status}',
  'users.updateFailed': 'Could not update',
  'users.new.title': 'New console user',
  'users.new.description':
    'A password is generated and shown once. Analysts can read everything and change nothing.',
  'users.role.analyst': 'analyst — read-only',
  'users.role.admin': 'admin — can author, run and publish',
  'users.role.psychometrician': 'psychometrician — can author and run, cannot publish',
  'users.create': 'Create user',
  'users.creating': 'Creating…',
  'users.createFailed': 'Could not create the user',
  'users.created.title': 'Account created',
  'users.created.description':
    'This password is shown once and cannot be retrieved — only its hash is stored. Hand it over out of band; the user should change it on first sign-in.',

  /* --------------------------------------------------- catalogue portability */
  'portability.export.title': 'Export this catalogue',
  'portability.export.description':
    'Modules, personas and matrix cells as one bundle. Results and instances are never included — they are records of something that happened in this environment, and copying them elsewhere would fabricate history.',
  'portability.includeItems': 'Include the item bank',
  'portability.exportAction': 'Export catalogue',
  'portability.exported': 'Catalogue exported',
  'portability.exported.detail': '{modules} modules, {personas} personas, {cells} cells.',
  'portability.exported.items': 'Plus {items} items.',
  'portability.exported.excluded':
    'No results or instances — those are records of what happened here.',
  'portability.import.title': 'Import a catalogue',
  'portability.import.description':
    'Dry-run first, always. Versions travel as they arrive — a module keeps its scoringConfig.version and a persona its planVersion — and a bundle carrying a version older than this environment’s is refused rather than applied.',
  'portability.bundleFile': 'Bundle file',
  'portability.checking': 'Checking the bundle…',
  'portability.notJson':
    'That file is not JSON. A catalogue bundle is the file this screen exports.',
  'portability.toCreate': '{count} to create',
  'portability.toUpdate': '{count} to update',
  'portability.unchanged': '{count} unchanged',
  'portability.conflicts': '{count} conflicts',
  'portability.from': 'From {source}',
  'portability.blocked': 'Nothing will be written while any entity conflicts.',
  'portability.column.kind': 'Kind',
  'portability.column.id': 'Id',
  'portability.column.action': 'Action',
  'portability.apply': 'Apply import',
  'portability.applied': 'Imported',
  'portability.imported': 'Catalogue imported',
  'portability.importFailed': 'The import failed',
  'portability.kind.modules': 'modules',
  'portability.kind.personas': 'personas',
  'portability.kind.matrixCells': 'matrix cells',
  'portability.kind.questions': 'items',
  'portability.action.create': 'create',
  'portability.action.update': 'update',
  'portability.action.skip': 'skip',
  'portability.action.conflict': 'conflict',

  /* ------------------------------------------------------- module readiness */
  'readiness.availableNeeded': '{available} available / {needed} needed',
  'readiness.noItems':
    'This module has no items yet, so nothing can be stranded — it also cannot fill a cell until its bank is authored and certified.',
  'readiness.allFit.one': 'The single item still fits this module, and no cell changes colour.',
  'readiness.allFit.other':
    'All {count} items still fit this module, and no cell changes colour.',
  'readiness.alreadyAmber.one': '{count} cell was already amber before this change.',
  'readiness.alreadyAmber.other': '{count} cells were already amber before this change.',
  'readiness.goAmber.one':
    '{count} cell goes amber — that persona will generate a short instrument, or fail to generate at all.',
  'readiness.goAmber.other':
    '{count} cells go amber — those personas will generate a short instrument, or fail to generate at all.',
  'readiness.stranded':
    '{stranded} of {checked} items would no longer be scoreable by this module.',
  'readiness.strandedCertified.one':
    '{count} of them is certified, so it leaves the sampling pool.',
  'readiness.strandedCertified.other':
    '{count} of them are certified, so they leave the sampling pool.',
  'readiness.nothingDeleted':
    'Nothing is deleted. They stay in the bank and stay readable; they simply stop being something this module can serve.',
  'readiness.reason.type_unsupported': 'type no longer supported',
  'readiness.reason.dimension_dropped': 'dimension dropped from the config',
  'readiness.truncated':
    '…and {more} more. The list is truncated for display; the count above is not.',

  /* -------------------------------------------------------------- change log */
  'changeLog.checking': 'Checking the chain…',
  'changeLog.chainUnavailable': 'Chain status unavailable',
  'changeLog.verified':
    '{count} entries verified — every row still hashes to what it did when it was written',
  'changeLog.broken': 'Chain broken at entry {at}: {reason}',
  'changeLog.column.when': 'When',
  'changeLog.column.what': 'What',
  'changeLog.column.who': 'Who',
  'changeLog.column.severity': 'Severity',
  'changeLog.filterByCategory': 'Filter by category',
  'changeLog.filterBySeverity': 'Filter by severity',
  'changeLog.filterByWho': 'Filter by who…',
  'changeLog.filterByActor': 'Filter by actor',
  'changeLog.allCategories': 'All categories',
  'changeLog.allSeverities': 'All severities',
  'changeLog.empty.title': 'Nothing recorded yet',
  'changeLog.empty.description':
    'Every mutating action writes one entry here. An empty log means nothing has been changed — not that recording is off.',
  // The six audit categories, derived server-side from the event name.
  'changeLog.category.Auth': 'Auth',
  'changeLog.category.Design': 'Design',
  'changeLog.category.Delivery': 'Delivery',
  'changeLog.category.Scoring': 'Scoring',
  'changeLog.category.Integration': 'Integration',
  'changeLog.category.System': 'System',
  'changeLog.severity.info': 'info',
  'changeLog.severity.notice': 'notice',
  'changeLog.severity.warning': 'warning',
  'changeLog.severity.critical': 'critical',

  /* ---------------------------------------------------------------- settings */
  'settings.title': 'Settings & Administration',
  'settings.description':
    'Console appearance, accounts, role permissions, integration credentials and the audit trail.',
  'settings.tab.appearance': 'Appearance',
  'settings.tab.users': 'Console users',
  'settings.tab.catalogue': 'Catalogue',
  'settings.tab.rbac': 'Role permissions',
  'settings.tab.apiKeys': 'API keys',
  'settings.tab.webhooks': 'Webhooks',
  'settings.tab.changelog': 'Change log',
  'settings.tab.audit': 'Integration endpoints',
  'settings.noRecords': 'No records',
  'settings.appearance.description':
    'A theme sets the full token scale — palette, corner radius and typeface — so every screen, chart and dialog follows it. Applies immediately and is remembered on this device.',
  'settings.theme.hint': 'Ocean Breeze is the default — the palette the console was demonstrated in.',
  'settings.theme.matchSystem': 'Match my system',
  'settings.mode.hint':
    'Independent of the theme — every theme ships both a light and a dark palette. Also available from the icon in the top bar.',
  'settings.changelog.description':
    'Who changed what, and when. Every mutating action writes exactly one entry, and nothing in the platform can edit or remove one.',
  'settings.users.description':
    'Who can sign in, and what they may do. Analysts read; admins author and run.',
  'settings.catalogue.title': 'Catalogue portability',
  'settings.catalogue.description':
    'Move a catalogue between environments. An environment-level act, not an authoring one — importing rewrites the catalogue every result here was produced under.',
  'settings.rbac.title': 'Role permissions (RBAC)',
  'settings.rbac.description':
    'What each role may do. Read from the API, not written into this screen — this is the same map the server checks on every request.',
  // Was "psychometrician arrives with the full RBAC phase", which described a
  // column that had been rendered for three phases beside a role nobody could be
  // assigned. The role is real now, and this table is generated from
  // ROLE_CAPABILITIES rather than from six literals in Settings.tsx.
  'settings.rbac.note':
    'Enforced server-side on every request. Reading results and analytics is not listed because it is not a permission: every signed-in operator reads.',
  'settings.column.capability': 'Capability',
  'capability.items:author': 'Author and certify items',
  'capability.design:draft': 'Edit module and persona drafts, including scoring configuration',
  'capability.catalogue:publish': 'Publish modules and blueprints',
  'capability.assessments:run': 'Run and re-score assessments',
  'capability.qc:release': 'Release a QC hold',
  'capability.platform:manage': 'Manage users and API keys',
  'permission.requiresRole': 'Requires the {roles} role',
  'settings.keys.title': 'How keys are stored',
  'settings.keys.storage':
    'Only a hash of each key is persisted. The full value is shown once at issue time and cannot be recovered afterwards — rotation issues a new key rather than revealing the old one.',
  'settings.keys.scopes':
    'Keys are scoped per route group (assessments:write, results:read, reference:read), and every issue, rotation and revocation writes an audit event.',
  'settings.apiKeys.description':
    'The fallback credential path; OAuth2 client-credentials is preferred for consumer systems.',
  'settings.issueKey': 'Issue key',
  'settings.apiKeys.empty.title': 'No API keys issued',
  // Was "…a real operation that lands with the integration phase", written while
  // the button beside it was disabled. Both endpoints have been live since G2;
  // the screen was the only thing missing, and a sentence describing a future
  // that had already arrived is worse than no sentence.
  'settings.apiKeys.empty.description':
    'Register a consumer system, then issue it a scoped key. The full key is shown once at issue time and only its hash is kept.',

  /* ------------------------------------ integration consumers & keys (X3) */
  'settings.consumers.title': 'Consumer systems',
  'settings.consumers.register': 'Register consumer',
  'settings.consumers.empty.title': 'No consumer systems registered',
  'settings.consumers.empty.description':
    'A consumer is whatever calls /v1 — the NAFIS platform, or an integrator’s sandbox. Register one before issuing a key, because a key belongs to a consumer rather than to a person.',
  'settings.consumers.column.consumer': 'Consumer',
  'settings.consumers.column.webhook': 'Webhook',
  'settings.consumers.column.keys': 'Keys',
  'settings.consumers.keyCount': '{active} live of {total}',
  'settings.consumers.webhookConfigured': 'signed',
  'settings.consumers.webhookNone': 'none',
  'settings.consumers.status.active': 'active',
  'settings.consumers.status.inactive': 'inactive',
  'settings.consumers.deactivate': 'Deactivate',
  'settings.consumers.reactivate': 'Reactivate',
  'settings.consumers.updated': 'Consumer updated',
  'settings.consumers.deactivated.detail':
    'Every key of it now fails. The record is kept — deactivating is not deleting.',
  'settings.consumers.reactivated.detail': 'Its keys authenticate again.',
  'settings.consumers.updateFailed': 'Could not update the consumer',
  'settings.consumers.new.title': 'Register a consumer system',
  'settings.consumers.new.description':
    'The name is how this consumer appears in the audit trail. A webhook is optional — but a URL without a signing secret is an unsigned delivery, which this platform does not send, so the two travel together.',
  'settings.consumers.name': 'Name',
  'settings.consumers.namePlaceholder': 'NAFIS integration',
  'settings.consumers.webhookUrl': 'Webhook URL (optional)',
  'settings.consumers.webhookSecret': 'Signing secret',
  'settings.consumers.webhookSecret.hint':
    'At least 16 characters. Stored and never shown again; every delivery is signed with it using HMAC-SHA256.',
  'settings.consumers.secretRequired':
    'A signing secret is required when a webhook URL is set — deliveries are signed.',
  'settings.consumers.secretTooShort': 'The signing secret must be at least 16 characters.',
  'settings.consumers.nameRequired': 'A consumer needs a name — it is what the audit trail records.',
  'settings.consumers.create': 'Register',
  'settings.consumers.creating': 'Registering…',
  'settings.consumers.created': 'Consumer registered',
  'settings.consumers.createFailed': 'Could not register the consumer',

  'settings.keys.issuedKeys': 'Issued keys',
  'settings.keys.column.rotated': 'Rotated',
  'settings.keys.status.active': 'active',
  'settings.keys.status.revoked': 'revoked',
  'settings.keys.never': 'never',
  'settings.keys.issue.title': 'Issue a key to {consumer}',
  'settings.keys.issue.description':
    'The key appears on the next screen and nowhere else, ever. Only its hash is stored, so no screen and no support request can show it to you a second time.',
  'settings.keys.label': 'Label',
  'settings.keys.labelPlaceholder': 'NAFIS production',
  'settings.keys.labelHint':
    'What this credential is for. It is the only thing that distinguishes one key from another in the audit trail.',
  'settings.keys.scopes.legend': 'Scopes',
  'settings.keys.scopeRequired': 'Choose at least one scope — a key holding none can call nothing.',
  'settings.keys.issuing': 'Issuing…',
  'settings.keys.issueFailed': 'Could not issue the key',
  'settings.keys.issued.title': 'Key issued',
  'settings.keys.issued.notice':
    'This is the only time this key is shown. Copy it now into the consumer’s secret store — only a hash is kept here, so it cannot be recovered. A lost key is rotated, never retrieved.',
  'settings.keys.copyKey': 'Copy the key',
  'settings.keys.rotate': 'Rotate',
  'settings.keys.rotate.title': 'Rotate ‘{label}’?',
  'settings.keys.rotate.description':
    'A new secret is minted and shown once. The current one stops working immediately — there is no grace window, so the consumer has to be updated before its next call.',
  'settings.keys.rotating': 'Rotating…',
  'settings.keys.rotated.title': 'Key rotated',
  'settings.keys.rotated.notice':
    'This is the only time the new key is shown, and it cannot be recovered afterwards. The previous secret stopped working the moment this one was minted.',
  'settings.keys.rotateFailed': 'Could not rotate the key',
  'settings.keys.revoke': 'Revoke',
  'settings.keys.revoke.title': 'Revoke ‘{label}’?',
  'settings.keys.revoke.description':
    'The credential stops working immediately, with no grace window — the usual reason to revoke is that somebody else has it.',
  'settings.keys.revoke.recordKept':
    'The record is kept rather than deleted: which key made which call outlives the key, and the audit trail would be unreadable without it.',
  'settings.keys.revoking': 'Revoking…',
  'settings.keys.revoked': 'Key revoked',
  'settings.keys.revokeFailed': 'Could not revoke the key',
  // The three `/v1` scopes. English on the wire (33b) — the scope string itself
  // is what an integrator pastes into a config — with the sentence localised.
  'settings.scope.assessments:write': 'Register candidates, start runs and submit answers',
  'settings.scope.results:read': 'Read results, candidate status and score envelopes',
  'settings.scope.reference:read': 'Read the module, persona and plan catalogue',
  'settings.webhooks.description':
    'Outbound delivery with at-least-once semantics, exponential backoff and a dead-letter queue.',
  'settings.webhooks.empty.title': 'No webhook endpoints registered',
  'settings.webhooks.empty.description':
    'A consumer registers its URL and signing secret here. Deliveries are signed with HMAC-SHA256 so the receiver can verify authenticity.',
  'settings.audit.title': 'Integration endpoints & audit',
  'settings.audit.description':
    'Append-only and hash-chained — each row carries the hash of its predecessor, so tampering is detectable.',
  'settings.audit.empty.title': 'No audit events yet',
  'settings.audit.empty.description':
    'Every mutating admin action writes exactly one audit row through the shared event emitter, so the trail cannot drift from what actually happened.',
  'settings.column.label': 'Label',
  'settings.column.key': 'Key',
  'settings.column.scopes': 'Scopes',
  'settings.column.created': 'Created',
  'settings.column.endpoint': 'Endpoint',
  'settings.column.event': 'Event',
  'settings.column.delivery': 'Delivery',
  'settings.column.timestamp': 'Timestamp (GST)',
  'settings.column.actor': 'Actor',
  'settings.column.category': 'Category',
  'settings.column.sourceIp': 'Source IP',

  /* ---------------------------------------------------------- notifications */
  'notifications.description':
    'Platform events requiring attention — delivery failures, quality flags, access requests and design changes.',
  'notifications.markAllRead': 'Mark all as read',
  'notifications.allMarkedRead': 'All notifications marked as read',
  'notifications.settings': 'Notification settings',
  'notifications.search': 'Search notifications',
  'notifications.searchPlaceholder': 'Search notifications…',
  'notifications.tab.all': 'All ({count})',
  'notifications.tab.unread': 'Unread ({count})',
  'notifications.noMatches': 'Nothing matches',
  'notifications.noMatches.description': 'No notification matches the current search or filter.',
  'notifications.none': 'No notifications',
  'notifications.none.description':
    'Quality-control flags, failed webhook deliveries and import errors surface here once those subsystems are live.',
  'notifications.markRead': 'Mark read',
  'notifications.accepted': 'Accepted · recorded in this session only',
  'notifications.declined': 'Declined · recorded in this session only',
  'notifications.footnote':
    'These are illustrative events. The feed becomes live when the read API over the audit trail exists — the same hash-chained events that will trigger result.finalized webhooks.',
  'notifications.kind.result': 'Result',
  'notifications.kind.webhook': 'Delivery',
  'notifications.kind.quality': 'Quality',
  'notifications.kind.plan': 'Design',
  'notifications.kind.access': 'Access',

  /* ------------------------------------------------- personas and the matrix */
  'matrix.title': 'Personas & Matrix',
  'matrix.description':
    'Each audience, and how many items it receives from each module — checked live against the certified bank.',
  'matrix.amber.one': '{count} cell amber',
  'matrix.amber.other': '{count} cells amber',
  'matrix.allCovered': 'All cells covered',
  'matrix.loadFailed': 'Could not load the matrix',
  'matrix.noCell': 'no cell',
  'matrix.items.one': '{count} item',
  'matrix.items.other': '{count} items',
  'matrix.available': '{count} avail',
  'matrix.provenance':
    'Coverage is one aggregation for the whole grid, not a query per cell, and it judges eligibility the same way generation does — a cell whose pool query excludes most of the bank shows amber here rather than failing for the first candidate.',
  'matrix.cell.description':
    'How many items this audience receives from this module, and how they are drawn.',
  'matrix.itemCount': 'Item count',
  'matrix.itemCount.detail':
    'How many questions this audience is asked from this module. Up to 60, so an assessment stays a sensible length.',
  'matrix.shuffle': 'Shuffle presentation order per candidate',
  'matrix.balance': 'Balance the draw across dimensions',
  'matrix.poolSummary':
    '{available} certified items match this cell’s pool; {requested} requested.',
  'matrix.willRefuse':
    'Generation will refuse this cell until the bank grows or the count comes down.',
  'matrix.dimensionBalance': '{dimension}: {available} available, {needed} needed',
  'matrix.saveCell': 'Save cell',
  'matrix.cellSaved': 'Cell saved',
  'matrix.cellSaved.ok': '{available} certified items available for {requested}.',
  'matrix.cellSaved.amber':
    'Amber: only {available} certified items for {requested} requested.',
  'matrix.cellSaveFailed': 'Could not save the cell',

  'personas.new': 'New persona',
  'personas.empty.title': 'No personas defined',
  'personas.empty.description':
    'A persona carries a plan template of ordered modules. The matrix then decides how many items each audience receives.',
  'personas.empty.action': 'Create the first persona',
  'personas.editPlan': 'Edit plan',
  'personas.draftPending': 'draft pending',
  'personas.active': 'active',
  'personas.retiredState': 'retired',
  'personas.activeHint': 'New runs can start for this audience.',
  'personas.retiredHint':
    'Starts nothing new. Existing results stay readable and the id still resolves.',
  'personas.action.retire': 'Retire — stop new runs, reversibly',
  'personas.action.reactivate': 'Reactivate — offer this audience again',
  'personas.retired': "'{persona}' is retired",
  'personas.retired.detail.one':
    'No new run can start for this audience. {count} completed run still names it and stays readable — the id does not vanish.',
  'personas.retired.detail.other':
    'No new run can start for this audience. {count} completed runs still name it and stay readable — the id does not vanish.',
  'personas.reactivated': "'{persona}' is active again",
  'personas.reactivated.detail':
    'On plan {version} — the one it left on. Preview it if the catalogue moved underneath it.',
  'personas.reactivateFailed': 'Could not reactivate',

  /* -------------------------------------------------------------- analytics */
  'analytics.description':
    'Cohort profiles by persona and module — the evidence base for refreshing scoring norms.',
  'analytics.filterByPersona': 'Filter by persona',
  'analytics.allPersonas': 'All personas',
  'analytics.loadFailed': 'Could not load analytics',
  'analytics.empty.title': 'No scored results for this cohort',
  'analytics.empty.description':
    'Cohort charts populate once assessments have been scored. Widen the filters, or run one from Delivery → Take an assessment.',
  'analytics.results.one': '{count} result',
  'analytics.results.other': '{count} results',
  'analytics.dimensionProfile': 'Dimension profile',
  'analytics.dimensionProfile.hint':
    '{module} — mean score per dimension for this cohort. These are the figures a persona’s scoring norms should be refreshed from.',
  'analytics.noDimensions': 'No dimensions for this selection.',
  'analytics.funnel': 'Completion funnel',
  'analytics.funnel.hint': 'Where instances sit in the lifecycle, for this cohort.',
  'analytics.funnel.note':
    'Instance states are not filtered by persona — the instance feed is the source, and it counts every run rather than only scored ones.',
  'analytics.column.instances': 'Instances',
  'analytics.headlines': 'Most common headlines',
  'analytics.headlines.hint':
    'The headline each result reported, most frequent first. For the interests module this is the Holland code; for any other model it is whatever that model calls its headline — the screen does not know which.',
  'analytics.noHeadlines': 'No headlines for this selection.',
  'analytics.provenance':
    'Read from the same single aggregation the dashboard uses, filtered by persona and module — no second pipeline and no $lookup.',

  /* ----------------------------------------------------------------- result */
  'result.eyebrow': 'Delivery · Result',
  'result.loadFailed': 'Could not load this result',
  'result.qcFlags': 'Response-quality flags:',
  'result.qcFlags.hint':
    'These describe how the form was filled in, not what it says. Interpret the scores with care.',
  /* The two reads (N1). "Technical" rather than "Provenance" because the tab
     is the psychometrician's whole half of the screen, and provenance is the
     first card in it rather than the name of it. */
  'result.reads': 'Two reads of this result',
  'result.tab.interpretation': 'Interpretation',
  'result.tab.technical': 'Technical',
  'result.allDimensions': 'All dimensions',
  'result.allDimensions.hint': 'The same figures the chart above draws.',
  'result.narrative': 'Interpretation notes',
  'result.narrative.hint': 'What the instrument concluded about this profile.',
  'result.narrative.pending':
    'The wording for these has not been authored yet, so the band the instrument assigned is shown in its place.',
  'result.pathways': 'Recommended pathways',
  'result.pathways.hint':
    'Occupations this profile aligns with, from the ESCO edition stamped on this result.',
  'result.pathways.fit': 'Fit {fit}',
  'result.dimension': 'Dimension',
  'result.score': 'Score',
  'result.band': 'Band',
  /* The reference group (N3). The cohort is named ONCE per table, because it is
     a property of the norm table rather than of a dimension — and naming it is
     the whole point: "78th percentile" without "of what" is the same non-answer
     as no percentile at all. */
  'result.percentile': 'Percentile',
  'result.percentile.short': 'Percentile {value}',
  'result.percentile.cohort': 'Percentiles compare this candidate with {cohort}.',
  'result.percentile.cohortUnnamed':
    'Percentiles compare this candidate with the norm group applied when this result was scored; this result does not name it.',

  /* The norm-referenced profile. The shaded stripe is where half the reference
     group sits — a description, never a target. The chart draws percentiles and
     the interval is on the score scale, so the interval is NOT on this chart and
     the table below is where it lives. */
  'result.profile.typical':
    'The shaded band is where the middle half of the reference group sits, from the 25th to the 75th percentile.',

  /* The cyclic figure. Named for the shape, not for a model — the enum member is
     `hexagon` because the dimensions form a cycle, and an eight-dimension model
     would draw an octagon through the same component. */
  /* Rings rather than a polygon frame: a radar's dimensions are independent
     magnitudes, and straight edges between neighbours would assert the adjacency
     that only the cyclic figure is entitled to claim. */
  'result.radar.label': 'Profile across each dimension’s own axis',
  'result.hexagon.label': 'Profile drawn on the dimension cycle, where neighbouring dimensions are the most alike',
  'result.hexagon.top': 'Highest three: {dimensions}',

  /* The shape of the profile. Both numbers are reported with how to read them
     and WITHOUT a verdict — the cut for "undifferentiated" depends on the
     instrument and its norms, neither of which is calibrated here, so a badge
     saying so would be this console inventing a criterion. */
  'result.shape': 'Shape of the profile',
  'result.shape.hint': 'How to read the ranking above — how far apart the dimensions are, and how well the top two agree.',
  'result.shape.differentiation': 'Spread',
  'result.shape.differentiation.hint':
    'Highest dimension minus lowest, on the 0–100 scale. A narrow spread means the ranking rests on small differences.',
  'result.shape.consistency': 'Agreement of the top two',
  'result.shape.consistency.hint':
    'How close the two strongest dimensions sit on the cycle. Neighbours describe one direction; opposites describe two.',
  'result.shape.consistency.high': 'Neighbouring',
  'result.shape.consistency.medium': 'One apart',
  'result.shape.consistency.low': 'Opposite',
  /* The interval (N4), named by its BASIS and never as a "confidence interval".
     That phrase is a coverage claim, the envelope carries endpoints and no
     coverage level, and a reader who assumes the usual 95% reads a
     standard-error band as twice the range it is. So the header and the sentence
     are looked up per `basis` — and a basis this catalogue has never seen falls
     back to the two generic lines, which name no construction rather than
     printing the raw enum member. */
  'result.interval.header': 'Range',
  'result.interval.header.standard_error': 'Standard-error band',
  'result.interval.range': '{low} – {high}',
  'result.interval.hint':
    'The range shows how precisely this score is measured, on the same 0–100 scale as the score itself. It states no confidence level.',
  'result.interval.hint.standard_error':
    'The range is one standard error either side of the score, on the same 0–100 scale — a measure of how precisely it was estimated, not a confidence interval.',
  /* SE(θ) — technical read only, and the description exists to name the scale. */
  'result.precision': 'Estimate precision',
  'result.precision.hint':
    'θ and its standard error are on the logit scale the ability estimate is computed on — not the 0–100 scale the dimension scores use.',
  'result.precision.theta': 'θ',
  'result.precision.se': 'SE(θ)',
  'result.provenance': 'Provenance',
  'result.provenance.hint': 'Stamped when the result was finalised, and never recomputed on read.',
  'result.field.result': 'Result',
  'result.field.config': 'Scoring config',
  'result.field.instrument': 'Instrument (plugin)',
  'result.field.administeredIn': 'Administered in',
  'result.field.esco': 'ESCO edition',
  'result.field.supersedes': 'Supersedes',
  'result.field.finalised': 'Finalised',
  'result.field.narrativeKeys': 'Interpretation keys',
  'result.firstScoring': '— first scoring',
  'result.rescore': 'Re-score',
  'result.rescoring': 'Re-scoring…',
  'result.rescored': 'Re-scored',
  'result.rescored.detail':
    'New result {result} at config v{version}. The previous one is retained.',
  'result.rescoreFailed': 'Could not re-score',

  /* -------------------------------------------------------------- QC review */
  'qcReview.description':
    'Results that scored, then stopped. Their scores are withheld from the consumer until someone here releases them.',
  'qcReview.held.one': '{count} held',
  'qcReview.held.other': '{count} held',
  'qcReview.nothingHeld': 'Nothing held',
  'qcReview.column.candidate': 'Candidate',
  'qcReview.column.why': 'Why it is held',
  'qcReview.column.scored': 'Scored',
  'qcReview.flagHint.straight_lining': 'Nearly every scalar answer was the same value.',
  'qcReview.flagHint.speeding': 'Median response time was below the module’s threshold.',
  'qcReview.reviewResult': 'Review the result',
  'qcReview.release': 'Release',
  'qcReview.releasing': 'Releasing…',
  'qcReview.released': 'Released',
  'qcReview.released.detail':
    '{result} is now completed, and its score is served to the consumer from here on.',
  'qcReview.releaseFailed': 'Could not release',

  /* ------------------------------------------------ the decision (V2) */
  'qcReview.release.title': 'Release {result}',
  'qcReview.release.description':
    'Releasing completes the run and starts serving its score to the consumer. Say what you checked, so the decision can be read back when the score is questioned.',
  'qcReview.hold': 'Put back on hold',
  'qcReview.holding': 'Putting it back…',
  'qcReview.hold.title': 'Put {result} back on hold',
  'qcReview.hold.description':
    'Its score stops being served to the consumer again. This is the undo of a release, and nothing wider — a run that completed on its own merits was never released and cannot be held.',
  'qcReview.heldAgain': 'Put back on hold',
  'qcReview.heldAgain.detail':
    '{result} is withheld from the consumer again, and is back in the queue below.',
  'qcReview.holdFailed': 'Could not put it back on hold',
  'qcReview.reason.label': 'Why',
  'qcReview.reason.placeholder':
    'e.g. Response pattern matches the candidate’s prior module; timing plausible for a fast reader.',
  'qcReview.reason.hint':
    'At least {min} characters — say what you checked, not that you checked.',
  'qcReview.reason.counter': '{count} of {min}',
  'qcReview.reason.tooShort':
    'Too short. A reason must be at least {min} characters — “ok” and “reviewed” record nothing anyone could be held to later.',
  'qcReview.reason.tooLong':
    'Too long. Keep it under {max} characters; case notes belong on the result, not in an audit row that can never be edited.',
  'qcReview.source.machine': 'Flagged automatically',
  'qcReview.source.reviewer': 'Put back by a reviewer',
  'qcReview.reviewerBy': '{actor} · {at}',
  'qcReview.releasedHere.title': 'Released just now',
  'qcReview.releasedHere.description':
    'Decisions you took on this screen. A released run leaves the queue, so this is where it can still be put back — it is not stored, and it clears when you leave.',
  'qcReview.empty.title': 'Nothing is waiting for review',
  'qcReview.empty.description':
    'A run whose plugin raises a quality-control flag is held here instead of completing, and its score is withheld from the integration surface until it is released.',
  'qcReview.footnote':
    'A flag is a reason to look, not a verdict — a genuinely consistent respondent straight-lines. Releasing records who decided, and which flags they decided despite; it is in',
  'qcReview.footnote.link': 'Settings → Change log',

  /* --------------------------------------------------------------- start run */
  'startRun.description':
    'Internal QA harness — generates a real instance and runs it through the real pipeline.',
  'startRun.newRun': 'New run',
  'startRun.resumeHint':
    'Starting again for the same candidate and module resumes the open instance rather than drawing a new one.',
  'startRun.candidateRef': 'Candidate reference',
  'startRun.candidateRef.detail':
    'Any reference you use to identify this candidate elsewhere. The platform stores no names or personal details, only this.',
  'startRun.persona': 'Persona',
  'startRun.selectPersona': 'Select an audience',
  'startRun.selectModule': 'Select a module',
  'startRun.module.detail':
    'The module decides which questions are asked, how they are scored and how the result is drawn.',
  'startRun.start': 'Start',
  'startRun.generating': 'Generating…',
  'startRun.failed': 'Could not start the assessment',

  /* ------------------------------------------------------------- languages */
  // Named in the language itself, never translated — see the picker in Profile.
  'languageName.en-AE': 'English',
  'languageName.ar-AE': 'Arabic',

  /* --------------------------------------------------------- take-test harness
   * Split by WHO reads it. `run.*` strings rendered through `tRun` follow the
   * instance's `administeredLocale`; the rest address the operator. See the
   * comment on the two translators in `RunAssessment`.
   */
  'run.eyebrow': 'Delivery · Take test',
  'run.description': 'Internal QA harness — this exercises the real pipeline end to end.',
  'run.administeredIn': 'Administered in {language}',
  'run.questionOf': 'Question {n} of {total}',
  'run.percentAnswered': '{progress}% answered',
  'run.back': 'Back',
  'run.next': 'Next',
  'run.finish': 'Finish',
  'run.reviewAction': 'Review',
  'run.review.title': 'Review your answers',
  'run.review.description': 'Check anything you skipped before submitting.',
  'run.answeredOf': '{answered} of {total}',
  'run.unanswered': 'unanswered',
  'run.keepAnswering': 'Keep answering',
  'run.submit': 'Submit',
  'run.scoring': 'Scoring…',
  'run.autosave':
    'Answers save as you go. You can close this tab and return to the same link — progress is restored from the server, not from this browser.',
  'run.rankingHint': 'Tap in order of preference — most preferred first.',
  /** Shown where a slider's value would be, until the candidate actually moves it. */
  'run.slider.unanswered': 'Drag to answer',
  'run.submitted': 'Submitted and scored',
  'run.submitted.detail': 'Result {result}',
  'run.submitFailed': 'Could not submit',
  'run.incomplete': 'Some questions are unanswered',
  'run.incomplete.detail': '{count} still to answer — highlighted in the review list.',
  'run.loadFailed': 'Could not load this assessment',
  'run.expired': 'This assessment has expired. Instances expire after 14 days of inactivity.',
  'run.noItems': 'This assessment has no items',
  'run.noItems.description': 'Nothing was delivered.',
  'run.noRenderer': 'No renderer for item type {type}. Add one to RENDERERS.',

  /* ------------------------------------------- M2: distributions, not means
   * A mean with no spread reports the middle as if it described someone. These
   * strings exist so the console can say which of "we measured this" and "we
   * could not measure this yet" a blank shape means.
   */
  'distribution.title': 'Spread by dimension',
  'distribution.hint':
    'A mean cannot show a bimodal dimension — two clusters and a flat middle average out to the same number as everybody scoring alike.',
  'distribution.pick': 'Dimension',
  'distribution.none': 'No dimension in this module has a distribution yet.',
  'distribution.missing':
    'This API returned no distribution for this dimension. The mean above is all it sent.',
  'distribution.candidates.one': '{count} candidate',
  'distribution.candidates.other': '{count} candidates',
  'distribution.mean': 'Mean',
  'distribution.sd': 'Standard deviation',
  'distribution.sd.hint': 'Sample SD (n−1) — these results are a sample of who will sit the instrument, not the whole population.',
  'distribution.range': 'Range',
  'distribution.rangeValue': '{min} to {max}',
  'distribution.insufficient.title': 'Counted, not described',
  'distribution.insufficient.body':
    '{n} of the {minimum} candidates an estimate needs. The count is real and is shown; the shape is withheld, because a spread over a handful of people describes who happened to sit it rather than the dimension.',
  'distribution.histogram.title': 'Candidates per score band',
  'distribution.bar': 'Candidates',
  'distribution.column.band': 'Score band',
  'distribution.column.candidates': 'Candidates',
  'distribution.bin': '{from}–{to}',
  'distribution.rescored.title': 'This cohort has been re-scored',
  'distribution.rescored.body':
    'The mean is taken over {results} results but only {candidates} candidates stand behind them, so a re-scored run is counted more than once in it. The spread below counts each candidate once. This is a known defect in the mean; it is shown here rather than quietly corrected, because the two numbers answer different questions.',

  /* ------------------------------------------------------- M1: item analysis
   * What makes *certified* a claim rather than a state. The absence strings are
   * the load-bearing ones: three different reasons a statistic is missing, and
   * one blank cell for all three tells a psychometrician nothing.
   */
  'nav.itemAnalysis': 'Item Analysis',
  'itemAnalysis.title': 'Item analysis',
  'itemAnalysis.description':
    'What the bank actually did when candidates met it — the evidence behind a certified item.',
  'itemAnalysis.pickModule.title': 'Choose a module',
  'itemAnalysis.pickModule.description':
    'Item statistics are computed per module: a p-value from one instrument and a mean response from another share no scale and belong on no single screen.',
  'itemAnalysis.includeHeld': 'Include QC-held runs',
  'itemAnalysis.includeHeld.hint':
    'Held runs are in the population by default. Turning them off gives the cleaned analysis — and the hold rate is then withheld rather than reported as zero.',
  'itemAnalysis.runs.one': '{count} scored run',
  'itemAnalysis.runs.other': '{count} scored runs',
  'itemAnalysis.items.one': '{count} item',
  'itemAnalysis.items.other': '{count} items',
  'itemAnalysis.asOf': 'As of {at}',
  'itemAnalysis.floor':
    'Estimates need {minimum} answers. Below that the counts still show and the estimate is withheld: a p-value from three responses is noise printed in the same typeface as one from three hundred.',
  'itemAnalysis.loadFailed': 'Could not load the item analysis',
  'itemAnalysis.empty.title': 'Nothing has been delivered yet',
  'itemAnalysis.empty.description':
    'This module has no scored runs inside the current filter, so there is nothing to analyse. Deliver it to a candidate and the statistics appear here.',
  'itemAnalysis.column.item': 'Item',
  'itemAnalysis.column.responses': 'Answered',
  'itemAnalysis.column.central': 'Central tendency',
  'itemAnalysis.column.correlation': 'Item-total correlation',
  'itemAnalysis.column.timing': 'Time on item',
  'itemAnalysis.column.qcHold': 'QC hold rate',
  'itemAnalysis.answeredOf': '{answered} of {delivered} delivered',
  'itemAnalysis.scale': 'on {min}–{max}',
  'itemAnalysis.sd': 'SD {sd}',
  'itemAnalysis.correlation.hint':
    'Corrected: the item against the sum of the OTHER scorable items in the run, never against a total that includes itself.',
  'itemAnalysis.timing.quartiles': 'p25 {p25} · p75 {p75}',
  'itemAnalysis.timing.none': 'No timed responses',
  'itemAnalysis.qcHold.runs.one': '{count} held run',
  'itemAnalysis.qcHold.runs.other': '{count} held runs',
  'itemAnalysis.qcHold.excluded': 'Held runs excluded',
  'itemAnalysis.qcHold.excluded.hint':
    'Not zero. With held runs out of the population there is no rate to report — turn the filter back on to see it.',
  'itemAnalysis.detail.title': 'Item {item}',
  'itemAnalysis.detail.description': 'How the responses to this item were distributed.',
  'itemAnalysis.detail.options': 'Option distribution',
  'itemAnalysis.detail.rankingNote':
    'For a ranking item the count is how often the option was placed FIRST. The mean rank beside it is what the count alone cannot say: an option ranked second by everybody is invisible in a first-place count.',
  'itemAnalysis.column.option': 'Option',
  'itemAnalysis.column.chosen': 'Chosen',
  'itemAnalysis.column.meanRank': 'Mean rank',
  'itemAnalysis.openRow': 'Open the response distribution',

  /* The four central-tendency statistics. NEVER rendered as a bare number under
   * a shared heading — a p-value and a mean response are different measurements,
   * so each cell names its own. */
  'itemStatistic.p_value': 'p-value',
  'itemStatistic.p_value.hint':
    'The proportion of respondents who answered correctly — the classical difficulty index.',
  'itemStatistic.mean_response': 'Mean response',
  'itemStatistic.mean_response.hint':
    'The mean mark on the item’s own scale, raw. Reverse-keying is deliberately not applied: a ceiling effect is a property of what candidates actually marked.',
  'itemStatistic.mean_option_value': 'Mean option value',
  'itemStatistic.mean_option_value.hint':
    'The mean authored effectiveness of the option chosen. Graded, not right or wrong.',
  'itemStatistic.mean_anchor_level': 'Mean anchor level',
  'itemStatistic.mean_anchor_level.hint': 'The mean behavioural anchor selected.',

  /* The three absences. They are different facts and must not look alike. */
  'absence.not_meaningful': 'Not defined for this type',
  'absence.not_meaningful.hint':
    'Permanent. A {type} item has no defensible definition for this statistic, so no amount of extra data will produce one.',
  'absence.insufficient': '{answered} of {minimum} answers',
  'absence.insufficient.hint':
    'Withheld until there are enough responses to estimate from. More data fixes this.',
  'absence.no_variance': 'No variance to measure',
  'absence.no_variance.hint':
    'Enough responses, and defined for this type — but every respondent answered alike, so there was nothing to compute from. Reporting zero would read as a finding.',

  /* ---------------------------------------------------- V1: QC evidence
   * The reviewer is asked whether straight-lining means an invalid protocol or a
   * genuinely consistent responder. That question has an answer in the response
   * data and none at all in the score.
   */
  'qcEvidence.action': 'Evidence',
  'qcEvidence.title': 'Evidence for {result}',
  'qcEvidence.description':
    'What the detectors measured, and the run they measured it in. The decision is still a person’s.',
  'qcEvidence.loadFailed': 'Could not load the evidence',
  'qcEvidence.answeredOf': '{answered} of {items} answered',
  'qcEvidence.scoredUnder': 'Scored under config v{version} · instrument {instrument}',
  'qcEvidence.administeredIn':
    'Administered in {language}. The prompts below are what the candidate read, not the console’s language.',
  'qcEvidence.empty': 'This run has no items, so there is no pattern to show.',

  'qcEvidence.verdict.title': 'What each detector measured',
  'qcEvidence.fired': 'Fired',
  'qcEvidence.didNotFire': 'Did not fire',
  'qcEvidence.straightLining.title': 'Straight-lining',
  'qcEvidence.straightLining.stat':
    '{proportion} of {n} scalar items answered {value}, against a threshold of {threshold}',
  'qcEvidence.straightLining.belowMinimum':
    '{n} scalar items, needs {minimum}. The proportion is still an observation about the run, but the detector does not fire below the floor — two identical answers out of two is a coincidence, not a pattern.',
  'qcEvidence.straightLining.nothingAnswered':
    'No scalar item was answered with a number, so there is no proportion to report.',
  'qcEvidence.straightLining.denominator':
    'The denominator is scalar items only — a run that also carries forced-choice and ranking items is longer than this number.',
  'qcEvidence.speeding.title': 'Speeding',
  'qcEvidence.speeding.stat':
    'Median {median} across {n} timed responses, against a threshold of {threshold}',
  'qcEvidence.speeding.untimed': 'No response carried a time, so there is no median to compare.',
  'qcEvidence.speeding.below':
    '{count} of {n} timed responses fell under the threshold. Descriptive only — the median is what fires the flag, so a run with several fast items and a slow median is not speeding.',

  'qcEvidence.configGone.title': 'The thresholds cannot be shown',
  'qcEvidence.configGone.body':
    'The config this run was scored under (v{version}) is no longer retained, so there is nothing to compare the run against. Showing today’s thresholds instead would print a rule this run was never judged by. Everything that is an observation rather than a comparison — the answers, the times, the order — is below and is unaffected.',

  'qcEvidence.flags.stored': 'Recorded on the result',
  'qcEvidence.flags.recomputed': 'The same rule, re-run now',
  'qcEvidence.flags.none': 'None',
  'qcEvidence.flags.disagree.title': 'These two disagree',
  'qcEvidence.flags.disagree.body':
    'The flags stored on the result and the ones the same rule produces now are not the same set. That means the responses or the retained config changed after this run was scored. Report it before deciding — it is a finding about the platform, not about the candidate.',

  'qcEvidence.pattern.title': 'Response pattern, in delivery order',
  'qcEvidence.pattern.hint':
    'One point per scalar answer, at the position the candidate met it. A gap is an item that was not answered — never a zero.',
  'qcEvidence.pattern.answer': 'Scalar answer',
  'qcEvidence.pattern.repeated': 'The repeated value ({value})',
  'qcEvidence.pattern.axisPosition': 'Position in the run',
  'qcEvidence.pattern.axisValue': 'Answer',
  'qcEvidence.pattern.mixedScales':
    'These items do not share one response scale, so the heights are not comparable between them. Each answer is listed with its own scale in the table below.',
  'qcEvidence.pattern.noScalar':
    'Nothing to plot: no scalar item in this run was answered with a number. Every item is in the table below.',

  'qcEvidence.latency.title': 'Time on each item',
  'qcEvidence.latency.hint':
    'Milliseconds per item, in delivery order. The line is the threshold from the config this run was scored under.',
  'qcEvidence.latency.normal': 'Response time',
  'qcEvidence.latency.below': 'Under the threshold',
  'qcEvidence.latency.threshold': 'Threshold',
  'qcEvidence.latency.none': 'No response in this run carried a time.',
  'qcEvidence.latency.noThreshold':
    'No threshold line: the config this run was scored under is no longer retained, so nothing was compared.',

  'qcEvidence.table.title': 'Every item, in delivery order',
  'qcEvidence.column.position': '#',
  'qcEvidence.column.item': 'Item, as administered',
  'qcEvidence.column.answer': 'Answer',
  'qcEvidence.column.time': 'Time',
  'qcEvidence.column.key': 'Scoring key',
  'qcEvidence.unanswered': 'Not answered',
  'qcEvidence.reverseKeyed': 'reverse-keyed',
  'qcEvidence.scalar': 'Scalar',
  'qcEvidence.ms': '{ms} ms',
  'qcEvidence.keyNote':
    'The scoring keys are here because they are the evidence: a flat run inside one dimension is a plausible responder, and the same run across every dimension is not.',

  /* ---------------------------------------------------------------- support */
  'support.description': 'Where to get help with the administration console.',
  'support.body':
    'For platform issues, contact the ETCC service desk. When reporting a problem, include the traceId shown on any error screen — it links your report directly to the server logs, the audit trail and any outbound webhook for that action.',
} as const;

export type StringKey = keyof typeof en;

/**
 * A catalogue for one language.
 *
 * The index signature exists for **one** reason: Arabic needs plural categories
 * English does not have (`.zero`, `.two`, `.few`, `.many`), so `ar.ts` must be
 * allowed keys this file has never heard of. It costs the compile-time typo
 * check on those extras, which `strings.test.ts` takes back by asserting every
 * extra key in `ar` is a plural variant of a key that really exists.
 */
export type Catalogue = Record<StringKey, string> & Record<string, string>;
